import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { IconSquare, IconMap, IconBox, IconMountain, IconPalette, IconVideo, IconRoute, IconBike, IconRulerMeasure, IconRuler2 } from '@tabler/icons-react';
import { db, functions } from '../firebase';
import { doc, getDoc, updateDoc } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import StylePicker, { groupTemplatesByStyle } from './StylePicker';
import MediaSelectionTiles from './MediaSelectionTiles';
import { isZwiftMapStyleName } from '../utils/zwiftActivity';
import './AutoUploadSettings.css';

const STRAVA_CLIENT_ID = "159733";
const STRAVA_SIGNIN_REDIRECT_URI = typeof window !== 'undefined' ? window.location.origin + "/strava-auth-callback" : "";
const STRAVA_SCOPE = "activity:read,activity:read_all,activity:write,profile:read_all";


const AutoUploadSettings = ({ user }) => {
  const [enabled, setEnabled] = useState(false); // indoor / Zwift fix toggle
  const [realArtEnabled, setRealArtEnabled] = useState(false); // art maps toggle (independent of indoor fix)
  const [zwiftArtEnabled, setZwiftArtEnabled] = useState(false); // AI art for Zwift rides (sub-setting of the indoor fix)
  const [excludedStyleGroupNames, setExcludedStyleGroupNames] = useState([]);
  const [use3DMap, setUse3DMap] = useState(true);
  const [zwiftMapType, setZwiftMapType] = useState('both');
  const [mapOrientation, setMapOrientation] = useState('portrait');
  const [distanceUnit, setDistanceUnit] = useState('metric');
  // Auto-upload media selection: which of {generated, video, original} to post
  // to the Strava activity. Defaults to all three; the user must keep at least
  // one ticked.
  const [autoUploadMediaSelection, setAutoUploadMediaSelection] = useState({
    generated: true,
    video: true,
    original: true,
  });
  const [hasProfileScope, setHasProfileScope] = useState(true);
  const [templates, setTemplates] = useState([]);
  const [templatesError, setTemplatesError] = useState(null);
  const [isLoadingTemplates, setIsLoadingTemplates] = useState(true);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [tagCategories, setTagCategories] = useState({});
  const [activeTagCategory, setActiveTagCategory] = useState(null);
  const [activeTag, setActiveTag] = useState(null);
  // The "Zwift Map" style is never auto-picked by the backend, so it isn't
  // offered in the auto-upload style selection either.
  const styleGroups = useMemo(
    () => groupTemplatesByStyle(templates).filter(g => !isZwiftMapStyleName(g.name)),
    [templates]
  );

  const availableTagCategories = useMemo(() => {
    if (styleGroups.length === 0 || Object.keys(tagCategories).length === 0) return {};
    return Object.entries(tagCategories).reduce((acc, [category, tags]) => {
      const availableTags = tags.filter(tag =>
        styleGroups.some(group => Array.isArray(group.tags) && group.tags.includes(tag))
      );
      if (availableTags.length > 0) acc[category] = availableTags;
      return acc;
    }, {});
  }, [tagCategories, styleGroups]);

  useEffect(() => {
    const categoryNames = Object.keys(availableTagCategories);
    if (categoryNames.length === 0) {
      if (activeTagCategory !== null) setActiveTagCategory(null);
      if (activeTag !== null) setActiveTag(null);
      return;
    }
    if (!activeTagCategory || !availableTagCategories[activeTagCategory]) {
      setActiveTagCategory(categoryNames[0]);
      if (activeTag !== null) setActiveTag(null);
      return;
    }
    if (activeTag && !availableTagCategories[activeTagCategory]?.includes(activeTag)) {
      setActiveTag(null);
    }
  }, [availableTagCategories, activeTagCategory, activeTag]);

  const loadState = useCallback(async () => {
    if (!user) return;
    setIsLoading(true);
    try {
      const userDocRef = doc(db, 'users', user.uid);
      const userSnap = await getDoc(userDocRef);
      const userData = userSnap.data() || {};
      const auto = userData.settings?.autoUpload || {};
      // Indoor fix and art maps are independent toggles. New explicit fields win;
      // legacy docs fall back to the old master (`enabled`) / `zwiftOnly` semantics
      // so existing users keep their behavior (mirrors the backend handler).
      setEnabled(typeof auto.indoorFixEnabled === 'boolean' ? auto.indoorFixEnabled : auto.enabled === true);
      setRealArtEnabled(
        typeof auto.artEnabled === 'boolean' ? auto.artEnabled : (auto.enabled === true && auto.zwiftOnly !== true)
      );
      // Zwift art is a sub-setting of the indoor fix: strict read, no legacy fallback.
      setZwiftArtEnabled(auto.zwiftArtEnabled === true);
      setUse3DMap(auto.use3DMap !== false);
      // Indoor map versions: "both" unless the user explicitly narrowed it.
      setZwiftMapType(auto.zwiftMapType === '2d' || auto.zwiftMapType === '3d' ? auto.zwiftMapType : 'both');
      setMapOrientation(auto.mapOrientation === 'landscape' ? 'landscape' : 'portrait');
      setDistanceUnit(auto.distanceUnit === 'imperial' ? 'imperial' : 'metric');
      // Each include flag defaults to true; only an explicit `false` opts out.
      // Mirrors the backend defaults in auto_upload_handler.
      setAutoUploadMediaSelection({
        generated: auto.includeGeneratedMap !== false,
        video: auto.includeTransitionVideo !== false,
        original: auto.includeOriginalMap !== false,
      });
      setExcludedStyleGroupNames(Array.isArray(auto.excludedStyleGroupNames) ? auto.excludedStyleGroupNames : []);

      const tokenDocRef = doc(db, 'users', user.uid, 'stravaTokens', 'tokens');
      const tokenSnap = await getDoc(tokenDocRef);
      const tokenData = tokenSnap.data() || {};
      const scopeStr = typeof tokenData.scope === 'string' ? tokenData.scope : '';
      setHasProfileScope(scopeStr.includes('profile:read_all'));

      try {
        const tagDoc = await getDoc(doc(db, 'settings', 'styleTags'));
        if (tagDoc.exists() && tagDoc.data().categories) {
          setTagCategories(tagDoc.data().categories);
        }
      } catch (e) {
        console.warn('Error loading tag categories:', e);
      }

    } catch (error) {
      console.error('Error loading auto-upload settings:', error);
    } finally {
      setIsLoading(false);
    }
  }, [user]);

  useEffect(() => {
    loadState();
  }, [loadState]);

  useEffect(() => {
    if (isLoading) return;
    if (typeof window === 'undefined') return;
    const hash = window.location.hash;
    if (hash !== '#auto-upload' && hash !== '#zwift-auto-upload' && hash !== '#art-auto-upload' && hash !== '#distance-units') return;
    // The zwift/art/distance-units sub-anchors only render once auto-upload is
    // enabled; fall back to the top of the indoor cycling section otherwise.
    const el = document.getElementById(hash.slice(1)) || document.getElementById('auto-upload');
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [isLoading]);

  useEffect(() => {
    let cancelled = false;
    const loadTemplates = async () => {
      setIsLoadingTemplates(true);
      setTemplatesError(null);
      try {
        const getTemplatesFunc = httpsCallable(functions, 'getPromptTemplates');
        const result = await getTemplatesFunc();
        if (cancelled) return;
        const list = Array.isArray(result.data?.templates) ?
          result.data.templates.filter(t => t.enabled !== false) : [];
        setTemplates(list);
      } catch (err) {
        if (cancelled) return;
        console.error('Error loading templates:', err);
        setTemplatesError('Unable to load styles right now.');
      } finally {
        if (!cancelled) setIsLoadingTemplates(false);
      }
    };
    loadTemplates();
    return () => {
      cancelled = true;
    };
  }, []);

  const persistEnabled = async (nextEnabled) => {
    if (!user) return;
    setIsSaving(true);
    try {
      await updateDoc(doc(db, 'users', user.uid), {
        'settings.autoUpload.indoorFixEnabled': nextEnabled,
        // Mirror the legacy master so any external analytics still reading it
        // sees "auto-upload on at all". The backend reads the explicit fields.
        'settings.autoUpload.enabled': nextEnabled || realArtEnabled,
      });
      setEnabled(nextEnabled);
    } catch (error) {
      console.error('Error updating indoor-fix auto-upload:', error);
      alert('Failed to update setting. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  const persistArtEnabled = async (nextArtEnabled) => {
    if (!user) return;
    setIsSaving(true);
    try {
      await updateDoc(doc(db, 'users', user.uid), {
        'settings.autoUpload.artEnabled': nextArtEnabled,
        // Mirror the legacy master (see persistEnabled).
        'settings.autoUpload.enabled': enabled || nextArtEnabled,
      });
      setRealArtEnabled(nextArtEnabled);
    } catch (error) {
      console.error('Error updating art auto-upload:', error);
      alert('Failed to update setting. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  // Sub-setting of the indoor fix, so no legacy `enabled` mirror: the backend
  // only acts on it while indoorFixEnabled is true.
  const persistZwiftArtEnabled = async (nextZwiftArtEnabled) => {
    if (!user) return;
    setIsSaving(true);
    try {
      await updateDoc(doc(db, 'users', user.uid), {
        'settings.autoUpload.zwiftArtEnabled': nextZwiftArtEnabled,
      });
      setZwiftArtEnabled(nextZwiftArtEnabled);
    } catch (error) {
      console.error('Error updating Zwift art auto-upload:', error);
      alert('Failed to update setting. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  const persistExcludedList = async (nextList) => {
    if (!user) return;
    setIsSaving(true);
    try {
      await updateDoc(doc(db, 'users', user.uid), {
        'settings.autoUpload.excludedStyleGroupNames': nextList,
      });
      setExcludedStyleGroupNames(nextList);
    } catch (error) {
      console.error('Error updating excluded styles:', error);
      alert('Failed to update styles. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  const handleToggleStyleGroup = (group) => {
    const name = group?.name;
    if (!name) return;
    const currentSet = new Set(excludedStyleGroupNames);
    if (currentSet.has(name)) {
      currentSet.delete(name);
    } else {
      currentSet.add(name);
    }
    persistExcludedList(Array.from(currentSet));
  };

  const handleSelectAll = () => {
    if (excludedStyleGroupNames.length === 0) return;
    persistExcludedList([]);
  };

  const handleClearAll = () => {
    persistExcludedList(styleGroups.map(g => g.name));
  };

  const handleReconnectStrava = (e) => {
    e.preventDefault();
    const state = Math.random().toString(36).substring(2);
    localStorage.setItem('stravaOAuthState', state);
    const consentUrl = `https://www.strava.com/oauth/authorize?client_id=${STRAVA_CLIENT_ID}&redirect_uri=${encodeURIComponent(STRAVA_SIGNIN_REDIRECT_URI)}&response_type=code&scope=${encodeURIComponent(STRAVA_SCOPE)}&approval_prompt=force&state=${state}`;
    window.location.href = consentUrl;
  };

  const persistDimension = async (next3D) => {
    if (!user) return;
    setIsSaving(true);
    try {
      await updateDoc(doc(db, 'users', user.uid), {
        'settings.autoUpload.use3DMap': next3D,
      });
      setUse3DMap(next3D);
    } catch (error) {
      console.error('Error updating auto-upload dimension:', error);
      alert('Failed to update map type. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  const persistZwiftMapType = async (nextType) => {
    if (!user) return;
    setIsSaving(true);
    try {
      await updateDoc(doc(db, 'users', user.uid), {
        'settings.autoUpload.zwiftMapType': nextType,
      });
      setZwiftMapType(nextType);
    } catch (error) {
      console.error('Error updating Zwift map type:', error);
      alert('Failed to update Zwift map versions. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  // Toggle one of the auto-upload media flags. Refuses to turn off the last
  // remaining selected item, so the user always uploads at least one.
  const toggleAutoUploadMedia = async (key) => {
    if (!user) return;
    const next = { ...autoUploadMediaSelection, [key]: !autoUploadMediaSelection[key] };
    if (!next.generated && !next.video && !next.original) {
      // Don't allow all three off — silently no-op.
      return;
    }
    const previous = autoUploadMediaSelection;
    setAutoUploadMediaSelection(next);
    setIsSaving(true);
    try {
      await updateDoc(doc(db, 'users', user.uid), {
        'settings.autoUpload.includeGeneratedMap': next.generated,
        'settings.autoUpload.includeTransitionVideo': next.video,
        'settings.autoUpload.includeOriginalMap': next.original,
      });
    } catch (error) {
      console.error('Error updating auto-upload media selection:', error);
      setAutoUploadMediaSelection(previous);
      alert('Failed to update setting. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  const persistOrientation = async (nextOrientation) => {
    if (!user) return;
    setIsSaving(true);
    try {
      await updateDoc(doc(db, 'users', user.uid), {
        'settings.autoUpload.mapOrientation': nextOrientation,
      });
      setMapOrientation(nextOrientation);
    } catch (error) {
      console.error('Error updating auto-upload orientation:', error);
      alert('Failed to update orientation. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  const persistDistanceUnit = async (nextUnit) => {
    if (!user) return;
    setIsSaving(true);
    try {
      await updateDoc(doc(db, 'users', user.uid), {
        'settings.autoUpload.distanceUnit': nextUnit,
      });
      setDistanceUnit(nextUnit);
    } catch (error) {
      console.error('Error updating auto-upload distance unit:', error);
      alert('Failed to update distance units. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  const totalStyleCount = styleGroups.length;
  const includedCount = Math.max(0, totalStyleCount - excludedStyleGroupNames.length);
  const mixSummary = totalStyleCount > 0
    ? (includedCount === totalStyleCount
      ? `All ${totalStyleCount} styles in the mix. We'll pick a different one each time.`
      : includedCount === 0
        ? "No styles selected. Pick at least one to keep auto-upload running."
        : `${includedCount} of ${totalStyleCount} styles in the mix.`)
    : null;

  // Indoor fix and art maps are independent toggles; `realArtEnabled` is loaded
  // and persisted on its own (see persistArtEnabled).
  const effEnabled = enabled;
  const artEnabled = realArtEnabled;

  const handleMasterToggle = (checked) => persistEnabled(checked);
  const handleArtToggle = (checked) => persistArtEnabled(checked);

  return (
    <>
      {/* ===== Section 1: Indoor Cycling Management ===== */}
      <div className="settings-section" id="auto-upload">
        <h2>
          <IconBike size={24} style={{ color: '#FC5200' }} />
          Indoor Cycling Management
        </h2>
        <p className="section-description">
          Fix your indoor cycling maps on Strava automatically. Zwift rides show the real world
          map instead of the Solomon Islands.
        </p>

        <div className="preference-card" id="zwift-auto-upload">
          <div className="auto-upload-settings">
            <div className="auto-upload-row">
              <div className="auto-upload-info">
                <h4>
                  Fix my indoor cycling maps automatically
                </h4>
                <ul className="auto-upload-bullets">
                  <li>
                    <span className="auto-upload-bullet-icon">🔧</span>
                    <span>Zwift rides show the real world map instead of the Solomon Islands.</span>
                  </li>
                  <li>
                    <span className="auto-upload-bullet-icon">⚡</span>
                    <span>Fixed and uploaded to your Strava activity automatically, no extra clicks.</span>
                  </li>
                  <li>
                    <span className="auto-upload-bullet-icon">🎁</span>
                    <span>Free. We only change the activity photo, never your route, time, or stats.</span>
                  </li>
                  <li>
                    <span className="auto-upload-bullet-icon">🌐</span>
                    <span>Works on public and private Strava activities.</span>
                  </li>
                </ul>
              </div>
              <div className="auto-upload-control">
                {isLoading ? (
                  <div className="loading-spinner-small" />
                ) : (
                  <label className="toggle-switch">
                    <input
                      type="checkbox"
                      checked={effEnabled}
                      onChange={(e) => handleMasterToggle(e.target.checked)}
                      disabled={isSaving || !hasProfileScope}
                    />
                    <span className="toggle-slider" />
                  </label>
                )}
              </div>
            </div>

            {!isLoading && !hasProfileScope && (
              <div className="auto-upload-scope-warning">
                <p>Reconnect Strava to enable auto upload.</p>
                <a href="/settings" onClick={handleReconnectStrava} className="auto-upload-reconnect-link">
                  Reconnect Strava
                </a>
              </div>
            )}

            {effEnabled && (
              <>
                <div className="auto-upload-section" id="zwift-art-auto-upload">
                  <div className="auto-upload-row">
                    <div className="auto-upload-info">
                      <h3 className="auto-upload-section-heading">Art maps for Zwift rides</h3>
                      <p className="auto-upload-section-caption">
                        Also auto-generate an artistic map for every Zwift ride, posted alongside
                        the fixed world map. Uses your art style mix, map type and upload choices
                        from Maps auto-upload settings below.
                      </p>
                    </div>
                    <div className="auto-upload-control">
                      <label className="toggle-switch">
                        <input
                          type="checkbox"
                          checked={zwiftArtEnabled}
                          onChange={(e) => persistZwiftArtEnabled(e.target.checked)}
                          disabled={isSaving}
                        />
                        <span className="toggle-slider" />
                      </label>
                    </div>
                  </div>
                </div>

                <div className="auto-upload-section">
                  <h3 className="auto-upload-section-heading">Map versions</h3>
                  <p className="auto-upload-section-caption">
                    Which map versions your fixed indoor rides get: the 3D terrain render, the flat world map, or both.
                  </p>
                  <div className="auto-upload-pill-toggle">
                    <button
                      type="button"
                      className={`auto-upload-pill-option ${zwiftMapType === 'both' ? 'selected' : ''}`}
                      onClick={() => persistZwiftMapType('both')}
                      disabled={isSaving}
                    >
                      <IconMountain size={22} />
                      <span>3D + 2D</span>
                      {zwiftMapType === 'both' && <div className="auto-upload-pill-check">✓</div>}
                    </button>
                    <button
                      type="button"
                      className={`auto-upload-pill-option ${zwiftMapType === '3d' ? 'selected' : ''}`}
                      onClick={() => persistZwiftMapType('3d')}
                      disabled={isSaving}
                    >
                      <IconMountain size={22} />
                      <span>3D only</span>
                      {zwiftMapType === '3d' && <div className="auto-upload-pill-check">✓</div>}
                    </button>
                    <button
                      type="button"
                      className={`auto-upload-pill-option ${zwiftMapType === '2d' ? 'selected' : ''}`}
                      onClick={() => persistZwiftMapType('2d')}
                      disabled={isSaving}
                    >
                      <IconMap size={22} />
                      <span>2D only</span>
                      {zwiftMapType === '2d' && <div className="auto-upload-pill-check">✓</div>}
                    </button>
                  </div>
                </div>

              </>
            )}
          </div>
        </div>
      </div>

      {/* ===== Section 2: Maps auto-upload settings ===== */}
      <div className="settings-section">
        <h2>
          <IconPalette size={24} style={{ color: '#c4956a' }} />
          Maps auto-upload settings
        </h2>
        <p className="section-description">
          Auto-generate stylised art maps on your outdoor runs and rides, uploaded to Strava in
          the styles you choose. Independent from indoor cycling management, turn on either or both.
        </p>

        <div className="preference-card" id="art-auto-upload">
          <div className="auto-upload-settings">
            <div className="auto-upload-row">
              <div className="auto-upload-info">
                <h4>Auto-upload Mythic maps to Strava</h4>
                <ul className="auto-upload-bullets">
                  <li>
                    <span className="auto-upload-bullet-icon">⚡</span>
                    <span>A new map is generated as soon as you log a Strava activity.</span>
                  </li>
                  <li>
                    <span className="auto-upload-bullet-icon">📤</span>
                    <span>Maps are shared to your Strava activity automatically, no extra clicks.</span>
                  </li>
                  <li>
                    <span className="auto-upload-bullet-icon">🎁</span>
                    <span>Auto-uploaded maps are free.</span>
                  </li>
                  <li>
                    <span className="auto-upload-bullet-icon">🎬</span>
                    <span>Get a free transition video: your Strava map morphing into the generated artwork, with every upload.</span>
                  </li>
                  <li>
                    <span className="auto-upload-bullet-icon">🌐</span>
                    <span>Auto-upload works for public and private Strava activities.</span>
                  </li>
                </ul>
              </div>
              <div className="auto-upload-control">
                {!isLoading && (
                  <label className="toggle-switch">
                    <input
                      type="checkbox"
                      checked={artEnabled}
                      onChange={(e) => handleArtToggle(e.target.checked)}
                      disabled={isSaving}
                    />
                    <span className="toggle-slider" />
                  </label>
                )}
              </div>
            </div>

            {(artEnabled || (effEnabled && zwiftArtEnabled)) && (
              <>
                {!artEnabled && (
                  <p className="auto-upload-section-caption">
                    Art auto-upload for outdoor activities is off. These style and upload
                    choices currently apply to your Zwift art maps.
                  </p>
                )}
                <div className="auto-upload-style">
                  <StylePicker
                    heading="Styles in your auto-upload mix"
                    styleGroups={styleGroups}
                    multiSelect
                    excludedStyleGroupNames={excludedStyleGroupNames}
                    onToggleStyleGroup={handleToggleStyleGroup}
                    onSelectAll={handleSelectAll}
                    onClearAll={handleClearAll}
                    availableTagCategories={availableTagCategories}
                    activeTagCategory={activeTagCategory}
                    activeTag={activeTag}
                    onChangeTagCategory={(cat) => { setActiveTagCategory(cat); setActiveTag(null); }}
                    onChangeTag={(tag) => setActiveTag(tag)}
                    disabled={isSaving}
                    isLoading={isLoadingTemplates}
                    error={templatesError}
                  />
                  {mixSummary && (
                    <p className="auto-upload-mix-summary">{mixSummary}</p>
                  )}
                </div>

                <div className="auto-upload-section">
                  <h3 className="auto-upload-section-heading">Map type</h3>
                  <div className="auto-upload-pill-toggle">
                    <button
                      type="button"
                      className={`auto-upload-pill-option ${use3DMap ? 'selected' : ''}`}
                      onClick={() => persistDimension(true)}
                      disabled={isSaving}
                    >
                      <IconMountain size={22} />
                      <span>3D</span>
                      {use3DMap && <div className="auto-upload-pill-check">✓</div>}
                    </button>
                    <button
                      type="button"
                      className={`auto-upload-pill-option ${!use3DMap ? 'selected' : ''}`}
                      onClick={() => persistDimension(false)}
                      disabled={isSaving}
                    >
                      <IconBox size={22} />
                      <span>2D</span>
                      {!use3DMap && <div className="auto-upload-pill-check">✓</div>}
                    </button>
                  </div>
                </div>

                <div className="auto-upload-section">
                  <h3 className="auto-upload-section-heading">What to upload</h3>
                  <MediaSelectionTiles
                    tiles={[
                      { key: 'generated', icon: <IconPalette size={28} />, label: 'Generated map' },
                      { key: 'video', icon: <IconVideo size={28} />, label: 'Transition video' },
                      { key: 'original', icon: <IconRoute size={28} />, label: 'Original map' },
                    ]}
                    selection={autoUploadMediaSelection}
                    onToggle={toggleAutoUploadMedia}
                  />
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      {/* ===== Section 3: shared upload preferences (indoor fixes AND art maps) ===== */}
      {(effEnabled || artEnabled) && (
        <div className="settings-section">
          <h2>
            <IconSquare size={24} style={{ color: '#4a90d9' }} />
            Upload format &amp; units
          </h2>
          <p className="section-description">
            These apply to every map we upload automatically — indoor cycling fixes and art maps alike.
          </p>

          <div className="preference-card">
            <div className="auto-upload-settings">
              <div className="auto-upload-section">
                <h3 className="auto-upload-section-heading">Map format</h3>
                <div className="auto-upload-pill-toggle">
                  <button
                    type="button"
                    className={`auto-upload-pill-option ${mapOrientation === 'portrait' ? 'selected' : ''}`}
                    onClick={() => persistOrientation('portrait')}
                    disabled={isSaving}
                  >
                    <IconSquare size={22} />
                    <span>Portrait (4:5)</span>
                    {mapOrientation === 'portrait' && <div className="auto-upload-pill-check">✓</div>}
                  </button>
                  <button
                    type="button"
                    className={`auto-upload-pill-option ${mapOrientation === 'landscape' ? 'selected' : ''}`}
                    onClick={() => persistOrientation('landscape')}
                    disabled={isSaving}
                  >
                    <IconMap size={22} />
                    <span>Landscape</span>
                    {mapOrientation === 'landscape' && <div className="auto-upload-pill-check">✓</div>}
                  </button>
                </div>
              </div>

              <div className="auto-upload-section" id="distance-units">
                <h3 className="auto-upload-section-heading">Distance units</h3>
                <p className="auto-upload-section-caption">
                  Used in the stats panel on art maps. Indoor fixes show the world map without stats.
                </p>
                <div className="auto-upload-pill-toggle">
                  <button
                    type="button"
                    className={`auto-upload-pill-option ${distanceUnit === 'metric' ? 'selected' : ''}`}
                    onClick={() => persistDistanceUnit('metric')}
                    disabled={isSaving}
                  >
                    <IconRulerMeasure size={22} />
                    <span>Kilometers</span>
                    {distanceUnit === 'metric' && <div className="auto-upload-pill-check">✓</div>}
                  </button>
                  <button
                    type="button"
                    className={`auto-upload-pill-option ${distanceUnit === 'imperial' ? 'selected' : ''}`}
                    onClick={() => persistDistanceUnit('imperial')}
                    disabled={isSaving}
                  >
                    <IconRuler2 size={22} />
                    <span>Miles</span>
                    {distanceUnit === 'imperial' && <div className="auto-upload-pill-check">✓</div>}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default AutoUploadSettings;
